import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Tracker} from '@udemy/event-tracking';

import {
    usePopQuizAttemptStartMutation,
    usePopQuizCompletedQuizzesQuery,
    usePopQuizItemSearchQuery,
} from 'src/generated/graphql';
import {POP_QUIZ_SETTINGS, usePopQuizSettings} from 'src/lib/pop-quiz/pop-quiz-settings';

import {
    findLatestCompletedQuiz,
    isCapped,
    LONG_SNOOZE_DAYS,
    MIN_QUIZ_QUESTIONS,
    Phase,
    PopQuizContinueCourseSelected,
    PopQuizDismissed,
    PopQuizFeedbackRating,
    PopQuizFeedbackSubmitted,
    PopQuizItem,
    PopQuizModalProps,
    PopQuizPresented,
    PopQuizSnoozed,
    QuizData,
    QuizStarted,
} from './pop-quiz';
import {PopQuizPromptPhase} from './pop-quiz/pop-quiz-prompt-phase.react-component';
import {PopQuizQuizPhase} from './pop-quiz/pop-quiz-quiz-phase.react-component';
import {PopQuizResultsPhase} from './pop-quiz/pop-quiz-results-phase.react-component';
import {PopQuizSnoozedPhase} from './pop-quiz/pop-quiz-snoozed-phase.react-component';

// --- Component ---

export function PopQuizModal({
    areOtherModalsOpen,
    enablePopQuizPP,
}: PopQuizModalProps): React.ReactElement | null {
    const {getPopQuizSetting, setPopQuizSetting} = usePopQuizSettings();

    const [isOpen, setIsOpen] = useState(false);
    const [quizData, setQuizData] = useState<QuizData | null>(null);
    const [phase, setPhase] = useState<Phase>('prompt');
    const [questions, setQuestions] = useState<PopQuizItem[]>([]);
    const [correctCount, setCorrectCount] = useState(0);
    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [stopShowing, setStopShowing] = useState(false);

    // Allow ?forcePopQuiz on the URL to bypass all cap/de-dupe checks (dev/QA only).
    const forcePopQuiz =
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('showPopQuiz');

    const capped = forcePopQuiz ? false : isCapped(getPopQuizSetting);

    // Use GraphQL to find a completed quiz from recent enrollments
    const {data: enrollmentsData, isLoading} = usePopQuizCompletedQuizzesQuery(
        {},
        {enabled: enablePopQuizPP && !capped},
    );

    // Pre-fetch quiz items so they're ready when the prompt shows.
    const {data: itemSearchData, isFetching: isItemSearchFetching} = usePopQuizItemSearchQuery(
        {quizId: String(quizData?.quizId ?? '')},
        {enabled: !!quizData && enablePopQuizPP && !capped},
    );

    // Map GQL items to the internal PopQuizItem shape, filtering to supported types.
    const supportedItems = useMemo((): PopQuizItem[] => {
        const rawItems = itemSearchData?.quizItemSearch?.items ?? [];
        return rawItems.reduce<PopQuizItem[]>((acc, item) => {
            if (item.__typename === 'MultipleChoiceQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: false,
                    correctOptionIds: [item.correctOption.id],
                });
            } else if (item.__typename === 'MultiSelectQuizItem') {
                acc.push({
                    id: item.id,
                    text: item.text,
                    options: item.options,
                    isMultiSelect: true,
                    correctOptionIds: item.correctOptions.map((o) => o.id),
                });
            }
            return acc;
        }, []);
    }, [itemSearchData]);

    // Quiz attempt lifecycle mutations.
    // Calling these drives the server-side SimpleQuizAttempt* events that populate UDW learning days.
    const startAttempt = usePopQuizAttemptStartMutation();

    // Extract quiz data from enrollments once loaded, then auto-open the modal
    const hasProcessedRef = useRef(false);
    const hasShownRef = useRef(false);
    // Guards against duplicate snooze events if multiple dismiss paths fire before a re-render.
    const isSnoozingRef = useRef(false);
    useEffect(() => {
        if (isLoading || hasProcessedRef.current || capped) {
            return;
        }
        hasProcessedRef.current = true;
        const found = findLatestCompletedQuiz(enrollmentsData, getPopQuizSetting, forcePopQuiz);
        if (found) {
            setQuizData(found);
        }
    }, [enrollmentsData, isLoading, capped, getPopQuizSetting, forcePopQuiz]);

    // Show prompt once when quiz data is ready, items are loaded, and no other modals are open.
    // Gating on supportedItems ensures numberOfQuestions is available for the PopQuizPresented event.
    useEffect(() => {
        if (
            quizData &&
            !areOtherModalsOpen &&
            !hasShownRef.current &&
            !isItemSearchFetching &&
            supportedItems.length >= MIN_QUIZ_QUESTIONS
        ) {
            hasShownRef.current = true;
            Tracker.publishEvent(
                new PopQuizPresented(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                ),
            );
            setIsOpen(true);
        }
    }, [quizData, areOtherModalsOpen, isItemSearchFetching, supportedItems]);

    const recordShown = useCallback(() => {
        void setPopQuizSetting(
            POP_QUIZ_SETTINGS.lastShownDate,
            new Date().toISOString().slice(0, 10),
        );
    }, [setPopQuizSetting]);

    const recordQuizOffered = useCallback(() => {
        if (quizData) {
            void setPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId, String(quizData.quizId));
        }
    }, [quizData, setPopQuizSetting]);

    const recordDismissal = useCallback(() => {
        recordQuizOffered();
        recordShown();
    }, [recordQuizOffered, recordShown]);

    // If we have quiz items but fewer than the minimum required, bail out early and mark as shown.
    useEffect(() => {
        if (
            quizData &&
            !isItemSearchFetching &&
            supportedItems.length > 0 &&
            supportedItems.length < MIN_QUIZ_QUESTIONS &&
            !hasShownRef.current
        ) {
            hasShownRef.current = true;
            recordShown();
            setIsOpen(false);
        }
    }, [quizData, isItemSearchFetching, supportedItems, recordShown]);

    const onAccept = useCallback(async () => {
        if (!quizData || supportedItems.length === 0) {
            return;
        }

        // Start a server-side attempt record.
        // This triggers SimpleQuizAttemptStartedEvent on the backend, which feeds UDW learning days.
        // Non-fatal: if the mutation fails we still show the quiz, just without server-side tracking.
        let newAttemptId: string | null = null;
        try {
            const result = await startAttempt.mutateAsync({quizId: String(quizData.quizId)});
            newAttemptId = result?.quizAttemptStart?.id ?? null;
            Tracker.publishEvent(
                new QuizStarted(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                    Number(newAttemptId),
                ),
            );
        } catch {
            // Proceed without an attemptId — per-answer submissions will also be skipped
        }

        void setPopQuizSetting(POP_QUIZ_SETTINGS.lastQuizId, String(quizData.quizId));
        setAttemptId(newAttemptId);
        setQuestions(supportedItems);
        setPhase('quiz');
    }, [quizData, supportedItems, startAttempt, setPopQuizSetting]);

    const onComplete = useCallback((finalCorrectCount: number) => {
        setCorrectCount(finalCorrectCount);
        setPhase('results');
    }, []);

    const onClose = useCallback(() => {
        setIsOpen(false);
        setAttemptId(null);
        setQuestions([]);
        setCorrectCount(0);
        setStopShowing(false);
        isSnoozingRef.current = false;
        setPhase('prompt');
        recordDismissal();
    }, [recordDismissal]);

    // Handles "Not now" / "Exit" from the prompt phase.
    // Snoozes for 60 days if the checkbox is checked; otherwise fires a dismissed event and closes.
    const handlePromptDismiss = useCallback(() => {
        if (stopShowing && quizData && !isSnoozingRef.current) {
            isSnoozingRef.current = true;
            void setPopQuizSetting(
                POP_QUIZ_SETTINGS.snoozedAt,
                new Date().toISOString().slice(0, 10),
            );
            Tracker.publishEvent(
                new PopQuizSnoozed(
                    {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                    {id: quizData.courseId},
                    LONG_SNOOZE_DAYS,
                ),
            );
            setPhase('snoozed');
        } else {
            if (quizData) {
                Tracker.publishEvent(
                    new PopQuizDismissed(
                        {id: quizData.quizId, numberOfQuestions: supportedItems.length},
                        {id: quizData.courseId},
                    ),
                );
            }
            onClose();
        }
    }, [stopShowing, quizData, supportedItems, setPopQuizSetting, onClose]);

    // Handles "Exit" from the quiz or results phase.
    // Snoozes for 60 days if the checkbox is checked; otherwise closes normally (7-day cap).
    const handleSnoozingExit = useCallback(() => {
        if (stopShowing && quizData && !isSnoozingRef.current) {
            isSnoozingRef.current = true;
            void setPopQuizSetting(
                POP_QUIZ_SETTINGS.snoozedAt,
                new Date().toISOString().slice(0, 10),
            );
            Tracker.publishEvent(
                new PopQuizSnoozed(
                    {id: quizData.quizId, numberOfQuestions: questions.length},
                    {id: quizData.courseId},
                    LONG_SNOOZE_DAYS,
                ),
            );
            setPhase('snoozed');
        } else {
            onClose();
        }
    }, [stopShowing, quizData, questions, setPopQuizSetting, onClose]);

    const onFeedback = useCallback(
        (rating: PopQuizFeedbackRating) => {
            if (quizData) {
                Tracker.publishEvent(
                    new PopQuizFeedbackSubmitted(
                        {id: quizData.quizId, numberOfQuestions: questions.length},
                        {id: quizData.courseId},
                        rating,
                    ),
                );
            }
        },
        [quizData, questions.length],
    );

    const onContinueLearning = useCallback(() => {
        if (quizData && typeof window !== 'undefined') {
            Tracker.publishEvent(
                new PopQuizContinueCourseSelected(
                    {id: quizData.quizId, numberOfQuestions: questions.length},
                    {id: quizData.courseId},
                ),
            );
            recordShown();
            window.location.assign(quizData.courseUrl);
        }
    }, [quizData, questions.length, recordShown]);

    if (!enablePopQuizPP || !quizData) {
        return null;
    }

    return (
        <>
            {phase === 'prompt' && (
                <PopQuizPromptPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onAccept={onAccept}
                    onDismiss={handlePromptDismiss}
                />
            )}

            {phase === 'snoozed' && (
                <PopQuizSnoozedPhase
                    isOpen={isOpen}
                    onClose={() => {
                        recordDismissal();
                        setIsOpen(false);
                    }}
                />
            )}

            {phase === 'quiz' && (
                <PopQuizQuizPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    questions={questions}
                    attemptId={attemptId}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onComplete={onComplete}
                    onExit={handleSnoozingExit}
                />
            )}

            {phase === 'results' && (
                <PopQuizResultsPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    correctCount={correctCount}
                    totalQuestions={questions.length}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onExit={handleSnoozingExit}
                    onContinueLearning={onContinueLearning}
                    onFeedback={onFeedback}
                />
            )}
        </>
    );
}
